import React, { useCallback, useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { updateSettingsAction, fetchSolutionTypesForCourse } from "../actions/settingsActions";
import { MessageControl, DisplayControl, ButtonControl } from "./controls";
import Collapsible from "./Collapsible";
import LessonItem from "./lesson-item";
import SectionHeader from "./section-header";
import { useSelector } from "react-redux";

const CourseSettings = ({ settings, setSettings, endpoint, name }) => {
  const [solutionTypes, setSolutionTypes] = useState([]);
  const stateSettings = useSelector((state) => state.settings);
  const dispatch = useDispatch();

  // Fetch solution types when component mounts or settings change
  useEffect(() => {
    const fetchSolutionTypes = async () => {
      if (settings?.id) {
        const types = await fetchSolutionTypesForCourse(settings.id)();
        if (!types || types.length === 0) {
          console.warn('No solution types found.');
          setSolutionTypes([]);
        } else {
          setSolutionTypes(types);
        }
      }
    };

    fetchSolutionTypes();
  }, [settings]);

	const updateSettings = useCallback(
		(type, id, optionKey, value) => {
      console.log(`type ${type} id ${id} optionKey ${optionKey} value ${value}`)
			const updatedSettings = { ...settings };
			if (type === "course") {
				updatedSettings.options = {
					...updatedSettings.options,
					[optionKey]: value,
				};
				updateChildren(updatedSettings, type, optionKey, value);
			} else if (type === "unit") {
				const unitIndex = updatedSettings.units.findIndex(
					(unit) => unit.title === id
				);
				if (unitIndex !== -1) {
					updatedSettings.units[unitIndex] = {
						...updatedSettings.units[unitIndex],
						options: {
							...updatedSettings.units[unitIndex].options,
							[optionKey]: value,
						},
					};
					updateChildren(updatedSettings, type, optionKey, value, id);
				}
			} else if (type === "lesson") {
				updatedSettings.units = updatedSettings.units.map((unit) => ({
					...unit,
					lessons: unit.lessons.map((lesson) =>
						lesson.id === id
							? {
									...lesson,
									options: {
										...lesson.options,
										[optionKey]: value,
									},
							  }
							: lesson
					),
				}));
			}
      if(endpoint === 'user') {
        const index = stateSettings.settings.findIndex(element => element.id === settings.id)
        const newSettings = {...stateSettings};
        newSettings.settings[index] = updatedSettings;
        dispatch(updateSettingsAction(newSettings.settings));
      } else {
        dispatch(updateSettingsAction(updatedSettings));
      }
    },
		[dispatch, settings]
	);

	const updateChildren = (settings, type, optionKey, value, id = null) => {
		if (type === "course") {
			settings.units = settings.units.map((unit) => ({
				...unit,
				options: {
					...unit.options,
					[optionKey]: value,
				},
				lessons: unit.lessons.map((lesson) => ({
					...lesson,
					options: {
						...lesson.options,
						[optionKey]: value,
					},
				})),
			}));
		} else if (type === "unit") {
			const unitIndex = settings.units.findIndex((unit) => unit.title === id);
			if (unitIndex !== -1) {
				settings.units[unitIndex] = {
					...settings.units[unitIndex],
					lessons: settings.units[unitIndex].lessons.map((lesson) => ({
						...lesson,
						options: {
							...lesson.options,
							[optionKey]: value,
						},
					})),
				};
			}
		}
	};

  const getOptionValue = useCallback(
    (type, id, optionKey) => {
      if (!settings) return false;

			if (type === "course") {
				return settings.options?.[optionKey] ?? false;
			} else if (type === "unit") {
				const unit = settings.units.find((unit) => unit.title === id);
				return unit ? unit.options[optionKey] : false;
			} else if (type === "lesson") {
				for (const unit of settings.units) {
					const lesson = unit.lessons.find((lesson) => lesson.id === id);
					if (lesson) {
						return lesson.options[optionKey];
					}
				}
			}
			return false;

    },
    [settings]
  );

  const renderSections = useMemo(() => {
    if (!settings || !settings.units) return null;
    return settings.units.map((section) => (
      <div key={section.id}>
        <Collapsible
          header={
            <SectionHeader
              section={section}
              updateSettings={updateSettings}
              getOptionValue={getOptionValue}
              solutionTypes={solutionTypes}
            />
          }
        >
          <div>
            {section.lessons.map((lesson) => (
              <LessonItem
                key={lesson.id}
                lesson={lesson}
                updateSettings={updateSettings}
                getOptionValue={getOptionValue}
                solutionTypes={solutionTypes}
              />
            ))}
          </div>
        </Collapsible>
      </div>
    ));
  }, [settings, updateSettings, getOptionValue, solutionTypes]);

  return (
    <div>
      <Collapsible
        header={
          <div className="section-container">
            <h4 className="section-heading">
              {settings?.name !== null ? name : "Course level"}
            </h4>
            <div className="section-wrapper">
            {solutionTypes.map((type) => (
                <div className="section-item-wrapper" key={type.id}>
                  <p>{type.name}</p>
                  <DisplayControl
                    value={getOptionValue("course", null, type.id)}
                    onChange={(value) =>
                      updateSettings("course", null, type.id, value)
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        }
      >
        {renderSections}
      </Collapsible>
    </div>
  );
};

CourseSettings.propTypes = {
  settings: PropTypes.object,
  setSettings: PropTypes.func.isRequired,
  endpoint: PropTypes.string.isRequired,
};

export { CourseSettings };
